import { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const translations = {
  en: {
    // Header
    brandName: "Space for Clarity",

    // Onboarding (landing) — single direct question, no eyebrow needed
    obQuestion: "What's on your mind?",
    obSub: "Describe a situation or recurring thought you'd like to understand better.",
    obPlaceholder: "Describe the situation in your own words...",
    obExamples: "For example: work · relationships · family · health · study · finances",
    obCta: "Start reflection",
    obReassurance: "Your decision remains yours. Private · No account.",

    // Conversational chrome
    greeting: "Welcome. Take your time.",
    step1Ack: "",
    newSession: "New session",
    recent: "Recent",
    noSessions: "Your past sessions will appear here.",
    untitledSession: "Untitled session",
    inputHint: "Enter to continue · Shift + Enter for a new line",
    inputHintFinal: "Enter to see your summary",

    // Feedback (v2 — research-oriented, testing phase)
    feedbackClosing1: "Thank you for taking the time to look at it.",
    feedbackClosing2: "What you do with it from here is yours.",
    feedbackIntro: "Thank you for taking the time to try Space for Clarity. Your feedback helps us understand how people experience the reflection and what we can improve. This will only take about a minute.",
    feedbackQ1: "At what moment, if any, did this experience start to feel meaningful?",
    feedbackQ1Placeholder: "Describe that moment if you can.",
    feedbackQ2: "Overall, how helpful was this experience?",
    feedbackQ2ScaleLow: "Not helpful at all",
    feedbackQ2ScaleHigh: "Extremely helpful",
    feedbackQ3: "Was anything confusing, unclear or missing?",
    feedbackQ3Placeholder: "Anything that interrupted or reduced the experience?",
    feedbackQ4: "If a friend asked what Space for Clarity is, how would you describe it?",
    feedbackQ4Placeholder: "Use your own words.",
    feedbackQ5: "Would you use Space for Clarity again for a real situation?",
    feedbackQ5Options: [
      { value: "yes",   label: "Yes" },
      { value: "maybe", label: "Maybe" },
      { value: "no",    label: "No" }
    ],
    feedbackQ6: "Is there anything else you'd like us to know?",
    feedbackQ6Placeholder: "Anything at all.",
    feedbackHint: "Just a gentle nudge — a helpfulness rating, whether you'd use it again, and a few words on one of the open questions would make this complete.",
    feedbackSubmit: "Share thoughts",
    feedbackSkip: "Skip",
    feedbackThanks: "Thank you. That's helpful.",
    prolificConfirm1: "Thank you — your responses have been saved.",
    prolificConfirm2: "You'll be redirected to Prolific in a few seconds to complete the study.",
    prolificReturnBtn: "Return to Prolific",
    // Conversation Fit Detection (v5.1 Sprint 2)
    convFitMsg1: "This reflection doesn't seem to be helping in its current form.",
    convFitMsg2: "You can stop here, or describe your situation differently and try again.",
    convFitTryAgain: "Try Again",
    convFitStop: "Stop Reflection",
    convFitClosing: "Thank you for taking a moment. Nothing has been saved from this conversation.",
    // Signal Behaviour (v6.3 Sprint 3B — grounded responses per Sprint 3A signal)
    signalMismatchMsg: "That question may not fit here. You can skip it if you want.",
    signalMismatchSkip: "Skip this question",
    signalMismatchTryAgain: "Try once more",
    signalStopMsg: "We can stop here.",
    signalStopFinish: "Finish",
    signalGnhMsg: "This doesn't seem to be helping in its current form. We can stop here, or you can start again with the situation in a different way.",
    signalGnhStopHere: "Stop here",
    signalGnhStartAgain: "Start again",
    signalSaturationMsg: "You've said that you've already thought about this a lot. We can stop here.",
    signalSaturationFinish: "Finish",
    // v6.4 Sprint 3C — grounded saturation completion (two states)
    signalSaturationObservationTitle: "What seems clear",
    signalSaturationObservationText: "You've already spent a lot of time thinking about this, and you've said that nothing is really shifting.",
    signalSaturationClosing: "You can leave it here for now.",
    signalSaturationMinimalMsg: "There may not be anything useful to add here right now. You can leave it here.",
    // v6.7 Sprint 4B — Reflection Fit boundary
    reflectionFitMsg1: "This sounds like something that may need an answer or explanation rather than reflection. I can't reliably answer that here.",
    reflectionFitMsg2: "If there is something about this situation that you are personally trying to make sense of, you can reflect on that instead.",
    reflectionFitReflect: "Reflect on what this brings up for me",
    reflectionFitLeave: "Leave it here",
    reflectionFitTransition: "What about this situation feels most unresolved for you personally?",
    reflectionFitLeaveClosing: "Thank you for taking a moment.",
    // v6.9 Sprint 5A — pre-summary measurement checkpoint (RESEARCH ONLY;
    // never influences the summary generation).
    preSummaryQuestion: "Compared with when you started, does anything about this feel different or clearer to you right now?",
    preSummaryOptionNo: "No",
    preSummaryOptionALittle: "A little",
    preSummaryOptionYes: "Yes",
    preSummaryOptionUnsure: "Not sure",
    preSummaryFollowUp: "What, if anything, feels different or clearer?",
    preSummaryFollowUpPlaceholder: "Share what feels different, if you'd like…",
    preSummaryContinue: "Continue",
    // v6.10 Sprint 5B — post-summary change-stage measurement (RESEARCH ONLY).
    reportedChangeStageQuestion: "Thinking about the reflection you just completed, when—if at all—did something begin to feel different or clearer?",
    reportedChangeStageDuringQuestions: "During the questions",
    reportedChangeStageSummary: "When I saw the summary",
    reportedChangeStageBoth: "Both",
    reportedChangeStageNoChange: "It didn't feel different or clearer",
    reportedChangeStageUnsure: "I'm not sure",
    // Clarity Measurement (v5.1 Sprint 3 — research-only)
    clarityBeforeTitle: "How clear does this situation feel to you right now?",
    clarityAfterTitle: "How clear does this situation feel to you now?",
    clarityScaleLow: "Not clear at all",
    clarityScaleHigh: "Completely clear",
    clarityContinue: "Continue",
    claritySkip: "Skip",

    // Legacy landing-page strings (kept for fallback / SEO meta)
    headline1: "What keeps coming back?",
    headline2: "Your decision remains yours.",
    subheadline: "A quiet space to think clearly. Without anyone telling you what to do.",
    ctaButton: "Begin the session",
    trustLine: "Private reflection · No account needed",
    howItWorks: "How it works",
    steps: [
      "Share what's on your mind",
      "Sit with what's difficult",
      "Notice what matters most",
      "See what truly matters"
    ],
    footer: "For personal and professional decisions",
    eyebrow: "A space to think clearly",
    footerNote: "— a quiet practice",
    
    // Flow page
    stepOf: "Step {current} of {total}",
    back: "Back",
    stepLabels: ["Context", "Reflect", "Values", "Summary"],
    
    // Step 1 - Externalisation
    step1Title: "What's still on your mind about this?",
    step1Subtitle: "Write whatever comes to mind. There are no right or wrong answers.",
    step1Placeholder: "Write whatever comes to mind. There are no right or wrong answers.",
    step1Button: "Continue",
    step1Loading: "Processing...",
    step1Examples: "Examples:",
    step1ExamplesList: [
      "Should I change jobs or stay where I am?",
      "Should I stay in this relationship or move on?",
      "Should I focus more on my health and change my lifestyle?",
      "Should I move to another city or stay where I am now?",
      "Should I buy a home now or keep my savings?"
    ],
    step1Note: "A space for everyday reflection.",
    
    // Step 2 - Understanding the difficulty
    step2Title: "What makes it difficult to find clarity about this?",
    step2Subtitle: "Take a moment. There's no need to answer perfectly.",
    step2Placeholder: "What's in the way is...",
    step2Button: "Continue",
    
    // Step 3 - Naming the tension
    step3Title: "Which two things feel hardest to hold together here?",
    step3Helper: "For example: freedom and security, rest and responsibility, or growth and stability.",
    step3Subtitle: "The two that pull in different directions.",
    step3Placeholder: "The two things that feel hardest to combine are...",
    step3Clarification: "Which two needs or priorities are difficult to combine in this situation?",
    step3Button: "Continue",
    
    // Step 4 - Values (protect framing)
    step4Title: "What would you most like to protect in this situation?",
    step4Subtitle: "Choose up to 3 values and briefly explain why they matter here.",
    step4Selected: "Selected",
    step4Suggestions: "Choose up to 3",
    step4AddOwn: "Add your own",
    step4CustomPlaceholder: "Other value...",
    step4AddButton: "Add",
    step4WhyPlaceholder: "What makes this important in this situation?",
    step4MaxReached: "Maximum 3 values selected",
    step4Button: "Continue",
    step4Skip: "Skip this step",
    
    // Step 5 - Landing (what's clearer now)
    step5Title: "What's clearer now than when you started?",
    step5Subtitle: "Even a small shift is worth noticing.",
    step5Placeholder: "Even a small shift is worth noticing.",
    step5Button: "See my summary",
    step5Loading: "Creating your summary...",
    
    // Summary page
    summaryTitle: "Your Structured Summary",
    summarySubtitle: "A clear view of your situation.",
    
    // Summary sections
    summaryOpening: "This isn't just a simple choice.",
    summaryOpeningLine2: "It's a situation where more than one direction makes sense — and that's what makes it unclear.",
    summaryTensionTitle: "You're pulled between",
    summaryTensionMiddle: "Both come with something you want to keep — and something you might have to give up.",
    summaryAboutTitle: "What this is about",
    summaryAboutIntro: "Looking at your answers, this situation seems to revolve around:",
    summaryAboutOutro: "These are the things shaping your decision.",
    summaryTradeoffTitle: "The trade-off",
    summaryTradeoffIntro: "There's no option where everything stays the same. Each direction shifts something:",
    summaryDirectionTitle: "Where you seem to lean",
    summaryObservationTitle: "What stands out",
    summaryDirectionIntro: "Right now, you seem to lean slightly toward:",
    summaryDirectionHesitation: "But not fully — because",
    summaryReflectionTitle: "A question to consider",
    summaryReflectionIntro: "If you look at this from a distance:",
    summaryReflectionQuestion: "Which shift feels more acceptable to you?",
    summaryReflectionOutro: "That's usually where clarity starts.",
    summaryClosingTitle: "Closing",
    summaryClosing1: "This doesn't give you the answer.",
    summaryClosing2: "But it shows you where to look.",
    summaryClosingOptional: "If something here feels true, that's worth paying attention to.",
    
    // Actions
    startOver: "Start a new reflection",
    done: "Done",
    
    // Reflection layer
    thoughtToSitWith: "A thought to sit with",
    thoughtToSitWithContent: "Which shift feels more acceptable to you?",
    
    // Return trigger
    beforeYouGo: "Before you go",
    beforeYouGoText: "You may notice new clarity about this situation after sitting with it for a while. If another decision comes to mind, you can structure it here as well.",
    structureAnother: "Structure another situation",
    
    // Values list
    values: [
      "Growth", "Security", "Freedom", "Balance", "Health", "Family", "Purpose"
    ],
    
    // Errors
    errorContextEmpty: "Please describe your situation",
    errorDifficultyEmpty: "Please describe what makes this difficult",
    errorTradeoffEmpty: "Please describe what you might have to give up",
    errorSelectValue: "Please select at least one value",
    errorDirectionEmpty: "Please describe the direction you're leaning toward",
    errorGeneric: "Something went wrong. Please try again.",
    
    // SurveyCircle
    surveyCirclePs: "PS: If you're coming from SurveyCircle, you will receive a completion code at the end.",
    surveyCircleTitle: "Completion",
    surveyCircleText: "If you're completing this as part of a survey, you can use the code below to confirm completion.",
    surveyCircleCopy: "Copy code",
    surveyCircleCopied: "Copied",
    surveyCircleRedeem: "Redeem code"
  },
  nl: {
    // Header
    brandName: "Space for Clarity",

    // Onboarding (landing) — direct, overthinker-aware question
    obQuestion: "Wat houdt je bezig?",
    obSub: "Beschrijf een situatie of terugkerende gedachte die je beter wilt begrijpen.",
    obPlaceholder: "Beschrijf de situatie in je eigen woorden...",
    obExamples: "Bijvoorbeeld: werk · relaties · familie · gezondheid · studie · financiën",
    obCta: "Reflectie starten",
    obReassurance: "De keuze blijft van jou. Privé · Geen account.",

    // Conversational chrome
    greeting: "Welkom. Neem je tijd.",
    step1Ack: "",
    newSession: "Nieuwe sessie",
    recent: "Recent",
    noSessions: "Je eerdere sessies verschijnen hier.",
    untitledSession: "Naamloze sessie",
    inputHint: "Enter om door te gaan · Shift + Enter voor een nieuwe regel",
    inputHintFinal: "Enter om je samenvatting te zien",

    // Feedback (v2 — research-oriented, testing phase)
    feedbackClosing1: "Dank dat je er even bij hebt stilgestaan.",
    feedbackClosing2: "Wat je er vanaf hier mee doet, is aan jou.",
    feedbackIntro: "Bedankt dat je de tijd hebt genomen om Space for Clarity te proberen. Met jouw feedback krijgen we beter inzicht in hoe mensen de reflectie ervaren en wat we kunnen verbeteren. Het invullen duurt ongeveer één minuut.",
    feedbackQ1: "Op welk moment begon deze ervaring, als dat gebeurde, echt betekenis voor je te krijgen?",
    feedbackQ1Placeholder: "Beschrijf dat moment als je wilt.",
    feedbackQ2: "Hoe behulpzaam vond je deze ervaring in het geheel?",
    feedbackQ2ScaleLow: "Helemaal niet behulpzaam",
    feedbackQ2ScaleHigh: "Zeer behulpzaam",
    feedbackQ3: "Was er iets verwarrend, onduidelijk of miste je iets?",
    feedbackQ3Placeholder: "Was er iets waardoor de ervaring minder prettig of minder duidelijk werd?",
    feedbackQ4: "Als een vriend of vriendin je zou vragen wat Space for Clarity is, hoe zou je het omschrijven?",
    feedbackQ4Placeholder: "Gebruik gerust je eigen woorden.",
    feedbackQ5: "Zou je Space for Clarity opnieuw gebruiken als je ergens echt mee zit?",
    feedbackQ5Options: [
      { value: "yes",   label: "Ja" },
      { value: "maybe", label: "Misschien" },
      { value: "no",    label: "Nee" }
    ],
    feedbackQ6: "Is er verder nog iets dat je met ons wilt delen?",
    feedbackQ6Placeholder: "Alles wat je wilt meegeven is welkom.",
    feedbackHint: "Kleine herinnering — een score voor behulpzaamheid, of je het opnieuw zou gebruiken, en een paar woorden bij één van de open vragen maken het compleet.",
    feedbackSubmit: "Deel je gedachten",
    feedbackSkip: "Overslaan",
    feedbackThanks: "Dank je. Dat helpt.",
    prolificConfirm1: "Bedankt — je antwoorden zijn opgeslagen.",
    prolificConfirm2: "Je wordt over enkele seconden teruggestuurd naar Prolific om het onderzoek af te ronden.",
    prolificReturnBtn: "Terug naar Prolific",
    // Conversation Fit Detection (v5.1 Sprint 2)
    convFitMsg1: "Deze manier van reflecteren lijkt op dit moment niet goed aan te sluiten.",
    convFitMsg2: "Je kunt hier stoppen, of je situatie opnieuw beschrijven.",
    convFitTryAgain: "Opnieuw proberen",
    convFitStop: "Reflectie beëindigen",
    convFitClosing: "Bedankt dat je even stilstond. Uit dit gesprek is niets bewaard.",
    // Signal Behaviour (v6.3 Sprint 3B — grounded responses per Sprint 3A signal)
    signalMismatchMsg: "Deze vraag past hier misschien niet goed. Je kunt hem overslaan als je wilt.",
    signalMismatchSkip: "Deze vraag overslaan",
    signalMismatchTryAgain: "Nog één keer proberen",
    signalStopMsg: "We kunnen hier stoppen.",
    signalStopFinish: "Afronden",
    signalGnhMsg: "Deze vorm lijkt je nu niet verder te helpen. We kunnen hier stoppen, of je kunt opnieuw beginnen en de situatie anders beschrijven.",
    signalGnhStopHere: "Hier stoppen",
    signalGnhStartAgain: "Opnieuw beginnen",
    signalSaturationMsg: "Je hebt aangegeven dat je hier al veel over hebt nagedacht. We kunnen hier stoppen.",
    signalSaturationFinish: "Afronden",
    signalSaturationObservationTitle: "Wat duidelijk lijkt",
    signalSaturationObservationText: "Je hebt hier al veel over nagedacht en je geeft aan dat er eigenlijk niets meer verschuift.",
    signalSaturationClosing: "Je kunt het hier voor nu laten.",
    signalSaturationMinimalMsg: "Misschien hoeft hier op dit moment niets meer aan toegevoegd te worden. Je kunt het hier laten.",
    // v6.7 Sprint 4B — Reflection Fit boundary
    reflectionFitMsg1: "Dit klinkt als iets waar eerder een antwoord of verklaring voor nodig is dan reflectie. Dat kan ik hier niet betrouwbaar beantwoorden.",
    reflectionFitMsg2: "Als er iets aan deze situatie is waar je zelf meer helderheid over wilt krijgen, kun je daar wel op reflecteren.",
    reflectionFitReflect: "Reflecteren op wat dit met mij doet",
    reflectionFitLeave: "Hier laten",
    reflectionFitTransition: "Wat voelt voor jou persoonlijk nog het meest onduidelijk aan deze situatie?",
    reflectionFitLeaveClosing: "Bedankt dat je even stilstond.",
    // v6.9 Sprint 5A — pre-summary measurement checkpoint (research only).
    preSummaryQuestion: "Vergeleken met toen je begon, voelt er nu iets anders of duidelijker voor je?",
    preSummaryOptionNo: "Nee",
    preSummaryOptionALittle: "Een beetje",
    preSummaryOptionYes: "Ja",
    preSummaryOptionUnsure: "Weet ik niet zeker",
    preSummaryFollowUp: "Wat voelt er, als er iets veranderd is, anders of duidelijker?",
    preSummaryFollowUpPlaceholder: "Deel gerust wat er anders voelt…",
    preSummaryContinue: "Doorgaan",
    // v6.10 Sprint 5B — post-summary change-stage measurement (research only).
    reportedChangeStageQuestion: "Als je terugkijkt op de reflectie die je net hebt gedaan: wanneer begon er, als dat gebeurde, iets anders of duidelijker te voelen?",
    reportedChangeStageDuringQuestions: "Tijdens de vragen",
    reportedChangeStageSummary: "Toen ik de samenvatting zag",
    reportedChangeStageBoth: "Beide",
    reportedChangeStageNoChange: "Het voelde niet anders of duidelijker",
    reportedChangeStageUnsure: "Weet ik niet zeker",
    // Clarity Measurement (v5.1 Sprint 3 — research-only)
    clarityBeforeTitle: "Hoe helder voelt deze situatie op dit moment voor je?",
    clarityAfterTitle: "Hoe helder voelt deze situatie nu voor je?",
    clarityScaleLow: "Helemaal niet helder",
    clarityScaleHigh: "Volledig helder",
    clarityContinue: "Doorgaan",
    claritySkip: "Overslaan",

    // Legacy landing strings
    headline1: "Waar blijf je over nadenken?",
    headline2: "De keuze blijft van jou.",
    subheadline: "Een rustige ruimte om helder te denken. Zonder dat iemand je vertelt wat te doen.",
    ctaButton: "Begin de sessie",
    trustLine: "Privé reflectie · Geen account nodig",
    howItWorks: "Hoe het werkt",
    steps: [
      "Vertel wat blijft hangen",
      "Sta stil bij wat moeilijk voelt",
      "Hoor wat het zwaarst weegt",
      "Zie wat echt belangrijk blijkt"
    ],
    footer: "Voor persoonlijke en professionele beslissingen",
    eyebrow: "Ruimte om helder te denken",
    footerNote: "— een stille gewoonte",
    
    // Flow page
    stepOf: "Stap {current} van {total}",
    back: "Terug",
    stepLabels: ["Context", "Reflectie", "Waarden", "Samenvatting"],
    
    // Step 1 - Externalisation
    step1Title: "Wat blijft er nog door je hoofd gaan?",
    step1Subtitle: "Schrijf op wat er in je opkomt. Er zijn geen goede of foute antwoorden.",
    step1Placeholder: "Schrijf op wat er in je opkomt. Er zijn geen goede of foute antwoorden.",
    step1Button: "Doorgaan",
    step1Loading: "Verwerken...",
    step1Examples: "Voorbeelden:",
    step1ExamplesList: [
      "Zal ik van baan veranderen of blijven waar ik nu zit?",
      "Zal ik in deze relatie blijven of verdergaan?",
      "Zal ik meer aandacht geven aan mijn gezondheid?",
      "Zal ik verhuizen of blijven waar ik nu woon?",
      "Zal ik nu een woning kopen of mijn spaargeld behouden?"
    ],
    step1Note: "Een ruimte voor alledaagse reflectie.",
    
    // Step 2 - Understanding the difficulty
    step2Title: "Wat maakt het lastig om hier helderheid over te krijgen?",
    step2Subtitle: "Neem even de tijd. Het hoeft niet perfect te zijn.",
    step2Placeholder: "Wat in de weg zit is...",
    step2Button: "Doorgaan",
    
    // Step 3 - Naming the tension
    step3Title: "Welke twee dingen zijn hier het moeilijkst met elkaar te verenigen?",
    step3Helper: "Bijvoorbeeld: vrijheid en zekerheid, rust en verantwoordelijkheid, of groei en stabiliteit.",
    step3Subtitle: "De twee die verschillende kanten op trekken.",
    step3Placeholder: "De twee dingen die het moeilijkst te verenigen zijn...",
    step3Clarification: "Welke twee behoeften of prioriteiten zijn hier moeilijk met elkaar te verenigen?",
    step3Button: "Doorgaan",
    
    // Step 4 - Values (protect framing)
    step4Title: "Wat zou je in deze situatie het liefst willen beschermen?",
    step4Subtitle: "Kies maximaal 3 waarden en leg kort uit waarom ze hier belangrijk zijn.",
    step4Selected: "Geselecteerd",
    step4Suggestions: "Kies maximaal 3",
    step4AddOwn: "Voeg eigen waarde toe",
    step4CustomPlaceholder: "Andere waarde...",
    step4AddButton: "Toevoegen",
    step4WhyPlaceholder: "Wat maakt dit belangrijk in deze situatie?",
    step4MaxReached: "Maximaal 3 waarden geselecteerd",
    step4Button: "Doorgaan",
    step4Skip: "Deze stap overslaan",
    
    // Step 5 - Landing (what's clearer now)
    step5Title: "Wat is er nu helderder dan toen je begon?",
    step5Subtitle: "Ook een kleine verandering is de moeite waard om op te merken.",
    step5Placeholder: "Ook een kleine verandering is de moeite waard om op te merken.",
    step5Button: "Bekijk mijn samenvatting",
    step5Loading: "Je samenvatting wordt gemaakt...",
    
    // Summary page
    summaryTitle: "Jouw Gestructureerde Samenvatting",
    summarySubtitle: "Een helder overzicht van je situatie.",
    
    // Summary sections
    summaryOpening: "Dit is niet zomaar een simpele keuze.",
    summaryOpeningLine2: "Het is een situatie waarin meerdere richtingen logisch zijn — en dat maakt het onduidelijk.",
    summaryTensionTitle: "Je wordt getrokken tussen",
    summaryTensionMiddle: "Beide komen met iets dat je wilt behouden — en iets dat je mogelijk moet opgeven.",
    summaryAboutTitle: "Waar dit over gaat",
    summaryAboutIntro: "Op basis van je antwoorden lijkt deze situatie te draaien om:",
    summaryAboutOutro: "Dit zijn de dingen die je beslissing vormgeven.",
    summaryTradeoffTitle: "De afweging",
    summaryTradeoffIntro: "Er is geen optie waarbij alles hetzelfde blijft. Elke richting verschuift iets:",
    summaryDirectionTitle: "Waar je naar lijkt te neigen",
    summaryObservationTitle: "Wat opvalt",
    summaryDirectionIntro: "Op dit moment lijk je licht te neigen naar:",
    summaryDirectionHesitation: "Maar niet volledig — omdat",
    summaryReflectionTitle: "Een vraag om te overwegen",
    summaryReflectionIntro: "Als je hier van een afstand naar kijkt:",
    summaryReflectionQuestion: "Welke verschuiving voelt acceptabeler voor je?",
    summaryReflectionOutro: "Daar begint meestal de helderheid.",
    summaryClosingTitle: "Afsluiting",
    summaryClosing1: "Dit geeft je niet het antwoord.",
    summaryClosing2: "Maar het laat je zien waar je moet kijken.",
    summaryClosingOptional: "Als iets hier klopt, is dat de moeite waard om op te letten.",
    
    // Actions
    startOver: "Start een nieuwe reflectie",
    done: "Klaar",
    
    // Reflection layer
    thoughtToSitWith: "Een gedachte om bij stil te staan",
    thoughtToSitWithContent: "Welke verschuiving voelt acceptabeler voor je?",
    
    // Return trigger
    beforeYouGo: "Voordat je gaat",
    beforeYouGoText: "Soms ontstaat er nieuwe helderheid wanneer je een situatie even laat bezinken. Als er nog een andere keuze door je hoofd speelt, kun je die hier ook structureren.",
    structureAnother: "Structureer een andere situatie",
    
    // Values list
    values: [
      "Groei", "Zekerheid", "Vrijheid", "Balans", "Gezondheid", "Familie", "Zingeving"
    ],
    
    // Errors
    errorContextEmpty: "Beschrijf alsjeblieft je situatie",
    errorDifficultyEmpty: "Beschrijf alsjeblieft wat dit lastig maakt",
    errorTradeoffEmpty: "Beschrijf alsjeblieft wat je mogelijk moet opgeven",
    errorSelectValue: "Selecteer alsjeblieft minimaal één waarde",
    errorDirectionEmpty: "Beschrijf alsjeblieft de richting waar je naar neigt",
    errorGeneric: "Er ging iets mis. Probeer het opnieuw.",
    
    // SurveyCircle
    surveyCirclePs: "PS: Kom je via SurveyCircle? Aan het einde ontvang je een code.",
    surveyCircleTitle: "Voltooiing",
    surveyCircleText: "Als je dit gebruikt als onderdeel van een onderzoek, kun je onderstaande code gebruiken om je deelname te bevestigen.",
    surveyCircleCopy: "Kopieer code",
    surveyCircleCopied: "Gekopieerd",
    surveyCircleRedeem: "Code inwisselen"
  },
  es: {
    // Header
    brandName: "Space for Clarity",

    // Onboarding (landing) — natural, low-friction question
    obQuestion: "¿Qué tienes en mente?",
    obSub: "Describe una situación o pensamiento recurrente que quieras entender mejor.",
    obPlaceholder: "Describe la situación con tus propias palabras...",
    obExamples: "Por ejemplo: trabajo · relaciones · familia · salud · estudios · finanzas",
    obCta: "Empezar la reflexión",
    obReassurance: "Tu decisión sigue siendo tuya. Privado · Sin cuenta.",

    // Conversational chrome
    greeting: "Bienvenido. Tómate tu tiempo.",
    step1Ack: "",
    newSession: "Nueva sesión",
    recent: "Reciente",
    noSessions: "Tus sesiones anteriores aparecerán aquí.",
    untitledSession: "Sesión sin título",
    inputHint: "Enter para continuar · Shift + Enter para una nueva línea",
    inputHintFinal: "Enter para ver tu resumen",

    // Feedback (v2 — research-oriented, testing phase)
    feedbackClosing1: "Gracias por tomarte un momento para observarlo.",
    feedbackClosing2: "Lo que hagas con ello a partir de aquí es tuyo.",
    feedbackIntro: "Gracias por tomarte el tiempo para probar Space for Clarity. Tus comentarios nos ayudan a comprender mejor cómo viven las personas esta experiencia y cómo podemos mejorarla. Solo te llevará aproximadamente un minuto.",
    feedbackQ1: "¿En qué momento, si ocurrió, empezó esta experiencia a tener verdadero significado para ti?",
    feedbackQ1Placeholder: "Descríbelo si te apetece.",
    feedbackQ2: "En general, ¿qué tan útil te resultó esta experiencia?",
    feedbackQ2ScaleLow: "Nada útil",
    feedbackQ2ScaleHigh: "Muy útil",
    feedbackQ3: "¿Hubo algo confuso, poco claro o que echaste en falta?",
    feedbackQ3Placeholder: "¿Hubo algo que interrumpiera o redujera la experiencia?",
    feedbackQ4: "Si un amigo te preguntara qué es Space for Clarity, ¿cómo lo describirías?",
    feedbackQ4Placeholder: "Descríbelo con tus propias palabras.",
    feedbackQ5: "¿Volverías a usar Space for Clarity para una situación real?",
    feedbackQ5Options: [
      { value: "yes",   label: "Sí" },
      { value: "maybe", label: "Tal vez" },
      { value: "no",    label: "No" }
    ],
    feedbackQ6: "¿Hay algo más que te gustaría compartir con nosotros?",
    feedbackQ6Placeholder: "Cualquier comentario es bienvenido.",
    feedbackHint: "Solo un pequeño recordatorio — una puntuación, si volverías a usarlo, y unas palabras en alguna de las preguntas abiertas completan el formulario.",
    feedbackSubmit: "Compartir tus ideas",
    feedbackSkip: "Saltar",
    feedbackThanks: "Gracias. Eso ayuda.",
    prolificConfirm1: "Gracias — tus respuestas se han guardado.",
    prolificConfirm2: "En unos segundos volverás a Prolific para completar el estudio.",
    prolificReturnBtn: "Volver a Prolific",
    // Conversation Fit Detection (v5.1 Sprint 2)
    convFitMsg1: "Esta forma de reflexionar no parece encajar en este momento.",
    convFitMsg2: "Puedes detenerte aquí, o describir tu situación de otra manera e intentarlo de nuevo.",
    convFitTryAgain: "Intentar de nuevo",
    convFitStop: "Terminar reflexión",
    convFitClosing: "Gracias por dedicar un momento. No se ha guardado nada de esta conversación.",
    // Signal Behaviour (v6.3 Sprint 3B — grounded responses per Sprint 3A signal)
    signalMismatchMsg: "Esta pregunta quizá no encaje aquí. Puedes omitirla si lo prefieres.",
    signalMismatchSkip: "Omitir esta pregunta",
    signalMismatchTryAgain: "Intentar una vez más",
    signalStopMsg: "Podemos parar aquí.",
    signalStopFinish: "Finalizar",
    signalGnhMsg: "Este formato no parece estar ayudándote ahora. Podemos parar aquí, o puedes comenzar de nuevo describiendo la situación de otra manera.",
    signalGnhStopHere: "Parar aquí",
    signalGnhStartAgain: "Comenzar de nuevo",
    signalSaturationMsg: "Has dicho que ya has pensado mucho sobre esto. Podemos parar aquí.",
    signalSaturationFinish: "Finalizar",
    signalSaturationObservationTitle: "Lo que parece claro",
    signalSaturationObservationText: "Ya has dedicado mucho tiempo a pensar en esto y dices que en realidad nada cambia.",
    signalSaturationClosing: "Puedes dejarlo aquí por ahora.",
    signalSaturationMinimalMsg: "Quizá no haya nada útil que añadir aquí en este momento. Puedes dejarlo aquí.",
    // v6.7 Sprint 4B — Reflection Fit boundary (equivalente natural, misma restraint)
    reflectionFitMsg1: "Esto parece algo que necesita más una respuesta o explicación que una reflexión. No puedo responder eso de forma fiable aquí.",
    reflectionFitMsg2: "Si hay algo de esta situación que tú mismo/a quieres entender mejor, puedes reflexionar sobre eso.",
    reflectionFitReflect: "Reflexionar sobre lo que esto me provoca",
    reflectionFitLeave: "Dejarlo aquí",
    reflectionFitTransition: "¿Qué de esta situación sientes personalmente que aún no está resuelto para ti?",
    reflectionFitLeaveClosing: "Gracias por dedicar un momento.",
    // v6.9 Sprint 5A — checkpoint de medición previo al resumen (solo research).
    preSummaryQuestion: "En comparación con cuando empezaste, ¿hay algo que ahora se sienta diferente o más claro para ti?",
    preSummaryOptionNo: "No",
    preSummaryOptionALittle: "Un poco",
    preSummaryOptionYes: "Sí",
    preSummaryOptionUnsure: "No estoy seguro/a",
    preSummaryFollowUp: "¿Qué se siente diferente o más claro, si hay algo?",
    preSummaryFollowUpPlaceholder: "Comparte lo que se sienta diferente, si quieres…",
    preSummaryContinue: "Continuar",
    // v6.10 Sprint 5B — medición de la fase de cambio posterior al resumen (solo research).
    reportedChangeStageQuestion: "Pensando en la reflexión que acabas de completar, ¿cuándo, si ocurrió, algo empezó a sentirse diferente o más claro?",
    reportedChangeStageDuringQuestions: "Durante las preguntas",
    reportedChangeStageSummary: "Cuando vi el resumen",
    reportedChangeStageBoth: "En ambos momentos",
    reportedChangeStageNoChange: "No se sintió diferente ni más claro",
    reportedChangeStageUnsure: "No estoy seguro/a",
    // Clarity Measurement (v5.1 Sprint 3 — research-only)
    clarityBeforeTitle: "¿Qué tan clara sientes esta situación en este momento?",
    clarityAfterTitle: "¿Qué tan clara sientes esta situación ahora?",
    clarityScaleLow: "Nada clara",
    clarityScaleHigh: "Completamente clara",
    clarityContinue: "Continuar",
    claritySkip: "Saltar",

    // Legacy landing strings
    headline1: "¿Qué te ronda la cabeza?",
    headline2: "Tu decisión sigue siendo tuya.",
    subheadline: "Un espacio tranquilo para pensar con claridad. Sin que nadie te diga qué hacer.",
    ctaButton: "Comenzar la sesión",
    trustLine: "Reflexión privada · Sin cuenta necesaria",
    howItWorks: "Cómo funciona",
    steps: [
      "Cuenta lo que te ronda la cabeza",
      "Detente en lo que pesa",
      "Reconoce lo que más importa",
      "Ve lo que de verdad importa"
    ],
    footer: "Para decisiones personales y profesionales",
    eyebrow: "Un espacio para pensar con claridad",
    footerNote: "— una práctica tranquila",
    
    // Flow page
    stepOf: "Paso {current} de {total}",
    back: "Atrás",
    stepLabels: ["Contexto", "Reflexión", "Valores", "Resumen"],
    
    // Step 1 - Externalisation
    step1Title: "¿Qué sigue rondando por tu cabeza sobre esto?",
    step1Subtitle: "Escribe lo que te venga a la mente. No hay respuestas correctas o incorrectas.",
    step1Placeholder: "Escribe lo que te venga a la mente. No hay respuestas correctas o incorrectas.",
    step1Button: "Continuar",
    step1Loading: "Procesando...",
    step1Examples: "Ejemplos:",
    step1ExamplesList: [
      "¿Debería cambiar de trabajo o quedarme donde estoy?",
      "¿Debería quedarme en esta relación o seguir adelante?",
      "¿Debería enfocarme más en mi salud?",
      "¿Debería mudarme o quedarme donde estoy?",
      "¿Debería comprar una casa ahora o conservar mis ahorros?"
    ],
    step1Note: "Un espacio para la reflexión cotidiana.",
    
    // Step 2 - Understanding the difficulty
    step2Title: "¿Qué hace que te resulte difícil encontrar claridad sobre esto?",
    step2Subtitle: "Tómate un momento. No hace falta responder a la perfección.",
    step2Placeholder: "Lo que está en el camino es...",
    step2Button: "Continuar",
    
    // Step 3 - Naming the tension
    step3Title: "¿Qué dos cosas se te hacen más difíciles de conciliar aquí?",
    step3Helper: "Por ejemplo: libertad y seguridad, descanso y responsabilidad, o crecimiento y estabilidad.",
    step3Subtitle: "Las dos que tiran en direcciones distintas.",
    step3Placeholder: "Las dos cosas que se me hacen más difíciles de combinar son...",
    step3Clarification: "¿Qué dos necesidades o prioridades son difíciles de combinar en esta situación?",
    step3Button: "Continuar",
    
    // Step 4 - Values (protect framing)
    step4Title: "¿Qué te gustaría proteger más en esta situación?",
    step4Subtitle: "Elige hasta 3 valores y explica brevemente por qué importan aquí.",
    step4Selected: "Seleccionados",
    step4Suggestions: "Elige hasta 3",
    step4AddOwn: "Agrega el tuyo",
    step4CustomPlaceholder: "Otro valor...",
    step4AddButton: "Agregar",
    step4WhyPlaceholder: "¿Qué hace que esto sea importante en esta situación?",
    step4MaxReached: "Máximo 3 valores seleccionados",
    step4Button: "Continuar",
    step4Skip: "Omitir este paso",
    
    // Step 5 - Landing (what's clearer now)
    step5Title: "¿Qué ves ahora con más claridad que al principio?",
    step5Subtitle: "Incluso un pequeño cambio merece ser reconocido.",
    step5Placeholder: "Incluso un pequeño cambio merece ser reconocido.",
    step5Button: "Ver mi resumen",
    step5Loading: "Creando tu resumen...",
    
    // Summary page
    summaryTitle: "Tu Resumen Estructurado",
    summarySubtitle: "Una vista clara de tu situación.",
    
    // Summary sections
    summaryOpening: "Esta no es solo una elección simple.",
    summaryOpeningLine2: "Es una situación donde más de una dirección tiene sentido — y eso es lo que la hace confusa.",
    summaryTensionTitle: "Te sientes atraído entre",
    summaryTensionMiddle: "Ambas vienen con algo que quieres mantener — y algo que podrías tener que renunciar.",
    summaryAboutTitle: "De qué se trata esto",
    summaryAboutIntro: "Mirando tus respuestas, esta situación parece girar en torno a:",
    summaryAboutOutro: "Estas son las cosas que dan forma a tu decisión.",
    summaryTradeoffTitle: "El intercambio",
    summaryTradeoffIntro: "No hay opción donde todo permanezca igual. Cada dirección cambia algo:",
    summaryDirectionTitle: "Hacia dónde pareces inclinarte",
    summaryObservationTitle: "Lo que destaca",
    summaryDirectionIntro: "Ahora mismo, pareces inclinarte ligeramente hacia:",
    summaryDirectionHesitation: "Pero no completamente — porque",
    summaryReflectionTitle: "Una pregunta para considerar",
    summaryReflectionIntro: "Si miras esto desde la distancia:",
    summaryReflectionQuestion: "¿Qué cambio te parece más aceptable?",
    summaryReflectionOutro: "Ahí es donde usualmente comienza la claridad.",
    summaryClosingTitle: "Cierre",
    summaryClosing1: "Esto no te da la respuesta.",
    summaryClosing2: "Pero te muestra dónde mirar.",
    summaryClosingOptional: "Si algo aquí se siente verdadero, vale la pena prestarle atención.",
    
    // Actions
    startOver: "Comenzar una nueva reflexión",
    done: "Listo",
    
    // Reflection layer
    thoughtToSitWith: "Un pensamiento para reflexionar",
    thoughtToSitWithContent: "¿Qué cambio te parece más aceptable?",
    
    // Return trigger
    beforeYouGo: "Antes de irte",
    beforeYouGoText: "Es posible que notes nueva claridad sobre esta situación después de reflexionar un poco. Si te viene a la mente otra decisión, también puedes estructurarla aquí.",
    structureAnother: "Estructurar otra situación",
    
    // Values list
    values: [
      "Crecimiento", "Seguridad", "Libertad", "Equilibrio", "Salud", "Familia", "Propósito"
    ],
    
    // Errors
    errorContextEmpty: "Por favor describe tu situación",
    errorDifficultyEmpty: "Por favor describe qué hace esto difícil",
    errorTradeoffEmpty: "Por favor describe qué podrías tener que renunciar",
    errorSelectValue: "Por favor selecciona al menos un valor",
    errorDirectionEmpty: "Por favor describe la dirección hacia la que te inclinas",
    errorGeneric: "Algo salió mal. Por favor intenta de nuevo.",
    
    // SurveyCircle
    surveyCirclePs: "PD: Si vienes desde SurveyCircle, recibirás un código al final.",
    surveyCircleTitle: "Completado",
    surveyCircleText: "Si estás completando esto como parte de una encuesta, puedes usar el código a continuación para confirmar tu participación.",
    surveyCircleCopy: "Copiar código",
    surveyCircleCopied: "Copiado",
    surveyCircleRedeem: "Canjear código"
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('blueprint-language');
    return saved || 'en';
  });

  useEffect(() => {
    localStorage.setItem('blueprint-language', language);
  }, [language]);

  const t = translations[language];

  const toggleLanguage = (lang) => {
    setLanguage(lang);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
